import React from "react";
import {
  Users, Car, Route, MessageSquare, Coins, BarChart3, Target, Radio, Terminal,
  LayoutDashboard, Activity, Upload, ShieldAlert, Clock, Gift, Bell, MapPin,
} from "lucide-react";
import { ASSETS } from "../config/brand";
import ThemeToggle from "../components/ThemeToggle";
import VoiceDemoButton from "../components/VoiceDemoButton";

const MODULES = [
  { id: "kpis",      label: "Panel Ejecutivo",  desc: "KPIs y analytics en vivo",         icon: LayoutDashboard, badge: "NUEVO", accent: "yellow" },
  { id: "live",      label: "Central en vivo",  desc: "Mapa de flota en tiempo real",     icon: Radio,           badge: "LIVE",  accent: "danger" },
  { id: "rides",     label: "Carreras",         desc: "Dispatch e historial",             icon: Route,           accent: "neon" },
  { id: "fleet",     label: "Flota",            desc: "Conductores y vehículos",          icon: Car,             accent: "indigo" },
  { id: "users",     label: "Usuarios",         desc: "Admin y pasajeros",                icon: Users,           accent: "indigo" },
  { id: "bulk",      label: "Operaciones masivas", desc: "🚀 Lanzamiento + Import + WhatsApp",  icon: Upload,          badge: "LANZAMIENTO", accent: "success" },
  { id: "tariffs",   label: "Tarifas y zonas",  desc: "Matriz de precios + polígonos",    icon: Coins,           accent: "yellow" },
  { id: "pricing",   label: "Franjas horarias", desc: "Recargos y descuentos por hora",   icon: Clock,           badge: "NUEVO", accent: "yellow" },
  { id: "promos",    label: "Promociones",      desc: "🎁 Bienvenida y descuentos con anti-fraude", icon: Gift,        badge: "NUEVO", accent: "danger" },
  { id: "notify",    label: "Notificaciones",   desc: "📢 Push masivo a pasajeros y conductores", icon: Bell,          badge: "NUEVO", accent: "info" },
  { id: "pois",      label: "Puntos TAXIGO",    desc: "📍 Hoteles, agencias, socios para el mapa", icon: MapPin,       badge: "NUEVO", accent: "success" },
  { id: "bot",       label: "Bot WhatsApp",     desc: "Conversaciones activas",           icon: MessageSquare,   accent: "success" },
  { id: "botlogs",   label: "Bot Logs · 2chat", desc: "Diagnóstico y payloads",           icon: Terminal,        accent: "indigo" },
  { id: "reports",   label: "Reportes",         desc: "Analytics y exportables",          icon: BarChart3,       accent: "neon" },
  { id: "sos",       label: "Alertas SOS",      desc: "Emergencias y respuesta",          icon: Target,          badge: "!",     accent: "danger" },
];

// Módulos restringidos a super_admin — sólo aparecen si el usuario tiene ese rol.
const SUPER_ADMIN_MODULES = [
  { id: "dedup",     label: "Dedup Guard",      desc: "Inspector del anti-duplicados",    icon: ShieldAlert,     badge: "DIAG",  accent: "danger" },
];

function ModuleCard({ mod, onClick }) {
  const Icon = mod.icon;
  return (
    <button
      data-testid={`launcher-mod-${mod.id}`}
      onClick={onClick}
      className="mdd-launcher-card text-left group animate-fade-up"
    >
      <div className="flex items-start justify-between mb-4 relative z-10">
        <div className="mdd-icon-wrap">
          <Icon className="w-6 h-6" strokeWidth={1.75} />
        </div>
        {mod.badge && (
          <span className={`mdd-chip ${
            mod.badge === "!"     ? "mdd-chip-danger animate-pulse-neon" :
            mod.badge === "LIVE"  ? "mdd-chip-danger" :
            mod.badge === "NUEVO" ? "mdd-chip-neon" : "mdd-chip-indigo"
          } !py-0.5 !px-2`}>
            {mod.badge}
          </span>
        )}
      </div>
      <div className="relative z-10">
        <div className="font-display font-semibold text-mdd-text text-base">{mod.label}</div>
        <div className="text-xs text-mdd-muted mt-1">{mod.desc}</div>
      </div>
    </button>
  );
}

export default function LauncherView({ onNavigate, user }) {
  // Concatena módulos base + módulos exclusivos de super_admin (si aplica).
  const isSuperAdmin = user?.role === "super_admin";
  const modules = isSuperAdmin ? [...MODULES, ...SUPER_ADMIN_MODULES] : MODULES;
  return (
    <div data-testid="launcher-view" className="min-h-screen flex flex-col">
      {/* HERO — logo + saludo + toggle theme */}
      <section className="max-w-[1680px] mx-auto w-full px-4 md:px-8 pt-10 pb-6">
        <div className="flex items-center gap-4 mb-2">
          <div className="w-16 h-16 rounded-2xl overflow-hidden ring-1 ring-white/10 bg-mdd-elevated shadow-lg shadow-black/40">
            <img src={ASSETS.logo} alt="Turismo MDD" className="w-full h-full object-contain" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-baseline gap-3 flex-wrap">
              <h1 className="font-display text-3xl md:text-4xl font-semibold tracking-tight text-mdd-text">
                Turismo MDD
              </h1>
              <span className="mdd-chip mdd-chip-neon">
                <span className="inline-block w-1.5 h-1.5 rounded-full bg-mdd-lime animate-pulse" />
                Online · v0.8
              </span>
            </div>
            <div className="text-sm text-mdd-muted mt-1">
              Bienvenido, <span className="text-mdd-text font-medium">{user?.name || user?.email}</span>
            </div>
          </div>
          <div className="hidden md:flex items-center gap-2">
            <ThemeToggle />
          </div>
        </div>
      </section>

      {/* GRID DE MÓDULOS — Odoo Apps style */}
      <section className="flex-1 max-w-[1680px] mx-auto w-full px-4 md:px-8 pb-16">
        <div className="flex items-baseline justify-between mb-5">
          <div>
            <div className="text-[10px] font-mono uppercase tracking-widest text-mdd-muted flex items-center gap-2">
              <Activity className="w-3 h-3" /> Aplicaciones
            </div>
            <h2 className="font-display text-xl font-semibold text-mdd-text tracking-tight">
              Selecciona un módulo
            </h2>
          </div>
          <div className="text-xs text-mdd-muted hidden md:block font-mono">
            {modules.length} módulos
          </div>
        </div>
        <div
          data-testid="launcher-grid"
          className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4"
        >
          {modules.map((m) => (
            <ModuleCard key={m.id} mod={m} onClick={() => onNavigate(m.id)} />
          ))}
        </div>
      </section>
      <VoiceDemoButton />
    </div>
  );
}
